import { Badge, Button } from '@fsd/ui';
import { Link, useRouterState } from '@tanstack/react-router';
import { ShoppingCart, Star } from 'lucide-react';
import { useTranslation } from '@/i18n/hooks';
import type { Drone } from '@/types/drones';
import { getLocalizedString, getLocalizedStringArray, useCart } from '@/utils';

interface ProductCardProps {
  drone: Drone;
  locale: 'pt-BR' | 'en-US' | 'es-ES';
  isUsed?: boolean;
  productType?: 'new' | 'used' | 'buffalo' | 'power-citros' | 'parts';
}

export function ProductCard({ drone, locale, isUsed = false, productType }: ProductCardProps) {
  const { t } = useTranslation('home');
  const { addItem, setIsOpen } = useCart();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  const name = getLocalizedString(drone.name, locale);
  const subtitle =
    getLocalizedString(drone.subtitle, locale) || getLocalizedString(drone.description, locale);
  const features = getLocalizedStringArray(drone.features, locale);
  const type = productType || (isUsed ? 'used' : 'new');
  const productUrl = `/product/${type}/${drone.id}`;

  const handleProductClick = () => {
    const origin = currentPath === '/catalog' ? 'catalog' : 'home';
    sessionStorage.setItem('productOrigin', origin);
  };

  const handleAddToCartClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(drone, 1, isUsed);
    setIsOpen(true);
  };

  return (
    <Link
      to={productUrl}
      onClick={handleProductClick}
      className="group relative flex h-full min-h-130 flex-col overflow-hidden rounded-[2.5rem] bg-white transition-all duration-500 hover:-translate-y-2 dark:bg-slate-900"
    >
      {/* Premium Glow Effect (appears on hover) */}
      <div className="absolute -inset-0.5 rounded-[2.5rem] bg-linear-to-r from-primary/30 to-secondary/30 opacity-0 blur-xl transition-opacity duration-500 group-hover:opacity-100" />

      <div className="relative flex flex-1 flex-col overflow-hidden rounded-[2.5rem] border border-slate-200/60 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] ring-1 ring-inset ring-white/10 transition-all duration-500 group-hover:border-primary/20 group-hover:shadow-[0_20px_50px_rgba(0,0,0,0.1)] dark:border-slate-800/60 dark:bg-slate-900 dark:ring-white/5">
        {/* Product Image Container */}
        <div className="relative h-72 w-full shrink-0 overflow-hidden">
          {/* Status Badges Overlay */}
          <div className="absolute inset-0 z-10 p-5">
            <div className="flex flex-col gap-2">
              {!drone.inStock ? (
                <div className="w-fit animate-fade-in backdrop-blur-md">
                  <Badge
                    variant="danger"
                    size="md"
                    className="bg-red-500/90 font-bold backdrop-blur-sm"
                  >
                    {isUsed ? t('usedDrones.outOfStock') : t('newDrones.outOfStock')}
                  </Badge>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {drone.featured && (
                    <Badge
                      variant="primary"
                      size="sm"
                      icon={<Star className="h-3 w-3 fill-current" />}
                      className="border border-amber-200/90 bg-linear-to-r from-amber-300/95 to-yellow-300/95 font-bold text-amber-950 shadow-sm shadow-amber-500/20 backdrop-blur-sm dark:border-primary/40 dark:from-primary/95 dark:to-emerald-400/95 dark:text-white dark:shadow-primary/30"
                    >
                      {t('productCard.featured')}
                    </Badge>
                  )}
                  {isUsed && drone.condition && (
                    <Badge
                      variant="secondary"
                      size="sm"
                      className="bg-secondary/90 font-bold backdrop-blur-sm"
                    >
                      {getLocalizedString(drone.condition, locale)}
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </div>

          <img
            src={drone.image}
            alt={name}
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
            loading="lazy"
          />

          {/* Subtle gradient overlay */}
          <div className="absolute inset-0 bg-linear-to-t from-black/20 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
        </div>

        {/* Product Content */}
        <div className="flex flex-1 flex-col p-6 sm:p-8">
          <div className="flex-1">
            {/* Category/Type Badge */}
            <div className="mb-4 flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-widest text-primary/80 dark:text-primary/60">
                {t(`productCard.types.${type}`)}
              </span>
              {typeof drone.rating === 'number' && drone.rating > 0 && (
                <div className="flex items-center gap-1.5 rounded-full bg-slate-50 px-2 py-1 dark:bg-slate-800/50">
                  <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                    {drone.rating.toFixed(1)}
                  </span>
                </div>
              )}
            </div>

            {/* Product Name */}
            <h3 className="mb-2 text-xl font-bold tracking-tight text-slate-900 transition-colors duration-300 group-hover:text-primary dark:text-slate-100 dark:group-hover:text-primary-light">
              {name}
            </h3>

            {/* Subtitle/Description */}
            <p className="mb-6 line-clamp-2 text-sm leading-relaxed text-slate-500 dark:text-slate-400">
              {subtitle}
            </p>

            {/* Features List */}
            {features.length > 0 && (
              <div className="mb-8 space-y-2.5">
                {features.slice(0, 3).map((feature) => (
                  <div
                    key={`feat-${drone.id}-${feature}`}
                    className="flex items-center gap-3 text-xs text-slate-600 dark:text-slate-400"
                  >
                    <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary dark:bg-primary/20">
                      <svg
                        className="h-3 w-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        role="img"
                        aria-label="Checkmark"
                      >
                        <title>Checkmark</title>
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <span className="font-medium line-clamp-1">{feature}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action Area */}
          <div className="mt-auto pt-6 border-t border-slate-100 dark:border-slate-800/50">
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-col">
                <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                  {drone.inStock ? t('newDrones.inStock') : t('newDrones.outOfStock')}
                </span>
                <span
                  className={`h-1.5 w-8 rounded-full mt-1 ${drone.inStock ? 'bg-green-500' : 'bg-red-500'}`}
                />
              </div>

              {drone.inStock && (
                <Button
                  onClick={handleAddToCartClick}
                  variant="primary"
                  size="sm"
                  className="rounded-full px-5 py-2.5 text-xs font-bold shadow-lg shadow-primary/20 transition-all duration-300 hover:scale-105 active:scale-95"
                >
                  <ShoppingCart className="mr-2 h-3.5 w-3.5" />
                  {t('productCard.buy')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
